/**
 * HTTP client for the Voleto engine.
 *
 * Auth is entirely cookie-based: the engine issues httpOnly, Secure,
 * SameSite=Strict cookies (`voleto_token`, 15 min; `voleto_refresh_token`,
 * 7 days) and JS can neither read nor set them. So there is no token to store,
 * attach, or refresh by hand — we only need `credentials: 'include'` on every
 * request and the browser does the rest. That is why there is no localStorage
 * anywhere in this file: storing a JWT there is exactly the XSS exposure the
 * engine's httpOnly design exists to prevent.
 *
 * The one piece of real logic is transparent re-authentication: when a call
 * fails with 401 because the 15-minute access token expired, we POST to
 * /auth/refresh once and replay the original request. Concurrent 401s share a
 * single in-flight refresh (single-flight) so a page issuing six requests at
 * once triggers one rotation, not six — which matters because the engine's
 * refresh tokens are theft-detecting and rotate on every use.
 */

import { API_BASE_URL } from '../config/env';

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }

  /** The caller is not authenticated (or the session was invalidated). */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Authenticated, but not permitted — RBAC or an approval gate blocked it. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Plain object; JSON-encoded automatically. Use `rawBody` to bypass. */
  body?: unknown;
  rawBody?: BodyInit | null;
  /** Skip the automatic 401 -> refresh -> retry cycle. */
  skipAuthRefresh?: boolean;
}

/** Endpoints where a 401 is a real answer, not an expired-token signal. */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

/**
 * Subscribe to unrecoverable session loss (refresh failed / token reuse
 * detected). AuthContext uses this to drop the user and send them to /login.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function emitSessionExpired(): void {
  for (const listener of sessionExpiredListeners) listener();
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  return await response.text();
}

function errorMessage(payload: unknown, response: Response): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const value = (payload as { error?: unknown }).error;
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  if (typeof payload === 'string' && payload.trim() !== '') return payload;
  return `Request failed with status ${response.status}`;
}

async function performRequest(path: string, options: RequestOptions): Promise<Response> {
  const { body, rawBody, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  let finalBody: BodyInit | null | undefined = rawBody;

  if (body !== undefined) {
    finalBody = JSON.stringify(body);
    if (!finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json');
    }
  }
  if (!finalHeaders.has('Accept')) finalHeaders.set('Accept', 'application/json');

  return fetch(buildUrl(path), {
    ...rest,
    headers: finalHeaders,
    body: finalBody,
    // The whole auth model depends on this: send and accept the engine's
    // httpOnly cookies. Without it every authenticated call is anonymous.
    credentials: 'include',
  });
}

/** Shared in-flight refresh, so N concurrent 401s cause exactly one rotation. */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await performRequest('/auth/refresh', { method: 'POST' });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Cleared on the microtask after settling so late joiners still share it.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

/**
 * Issue an API request. Resolves with the parsed body, throws `ApiError` on a
 * non-2xx response.
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { skipAuthRefresh, ...rest } = options;
  const allowRefresh =
    !skipAuthRefresh && !NO_REFRESH_PATHS.some((suffix) => path.startsWith(suffix));

  let response = await performRequest(path, rest);

  if (response.status === 401 && allowRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await performRequest(path, rest);
    } else {
      emitSessionExpired();
    }
  }

  const payload = await parseBody(response);

  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(payload, response), payload);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  // The engine uses PATCH for partial profile updates (venue, agency).
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};
